import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { addSiteData, getSiteById, updateSiteData, detectSiteExpiry } from "../services/siteAPI";
import { getServers } from "../services/serverAPI";
import AppMenu from "../components/AppMenu";
import { title } from "../config";
import Select from "react-select";
import { siteRunningStatus } from "../services/constant";
import { getUserDetails } from "../services/userStorage";
import { BackButton } from "../components/BackButton";
import { useToast } from "../components/ToastContext";

export default function AddSite(props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { recordId } = useParams();
  const [runningStatusOptions, setRunningStatusOptions] = useState([]);
  const [serverOptions, setServerOptions] = useState([]);
  const [error, setError] = useState('');
  const [isDetectingDates, setIsDetectingDates] = useState(false);
  const userDetails = getUserDetails();

  const [site, setSite] = useState({
    name: "",
    link: "",
    status: "",
    runningStatus: "",
    sslExpiry: "",
    domainExpiry: "",
    serverId: "",
    serverName: "",
    comment: "",
    contact: "",
  });

  const [buttonLabel, setBLabel] = useState("Add");

  const loadSite = async (id) => {
    let record = await getSiteById(id, showToast);
    if (record) {
      setSite(record);
      setBLabel("Update");
    }
  };

  const loadData = async () => {
    setRunningStatusOptions(siteRunningStatus);
    try {
      const serversData = await getServers(showToast);
      if (Array.isArray(serversData)) {
        setServerOptions(
          serversData.map((s) => ({
            value: s._id,
            label: `${s.name}${s.hostname ? ` (${s.hostname})` : ''}`,
            name: s.name,
          }))
        );
      }
    } catch (e) {
      console.error("Error loading server options:", e);
    }
  };

  useEffect(() => {
    if (recordId) {
      loadSite(recordId);
    }
    loadData();
  }, [recordId]);

  const handleChange = (e) => {
    setSite({ ...site, [e.target.name]: e.target.value });
  };

  const handleRunningStatusChange = (e) => {
    setSite({ ...site, runningStatus: e.value });
  };

  const handleServerChange = (selected) => {
    setSite({
      ...site,
      serverId: selected ? selected.value : "",
      serverName: selected ? selected.name : "",
    });
  };

  // Automatically detect SSL and Domain expiry dates from link
  const handleLinkBlur = async () => {
    const link = site.link?.trim();
    if (!link || link.length < 4) return;

    try {
      setIsDetectingDates(true);
      const res = await detectSiteExpiry(link);
      if (res && res.success) {
        setSite((prev) => ({
          ...prev,
          sslExpiry: res.sslExpiry ? new Date(res.sslExpiry).toISOString().split('T')[0] : prev.sslExpiry,
          domainExpiry: res.domainExpiry ? new Date(res.domainExpiry).toISOString().split('T')[0] : prev.domainExpiry,
        }));
        if (res.sslExpiry || res.domainExpiry) {
          showToast("SSL & Domain expiration dates auto-detected!", "success");
        }
      }
    } catch (err) {
      console.error("Auto-detect expiry error:", err);
    } finally {
      setIsDetectingDates(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!site.link || site.link.trim().length === 0) {
      setError('Link is required.');
      return;
    }

    const urlPattern = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z0-9]{2,4}(\/[^\s]*)?$/i;
    if (!urlPattern.test(site.link)) {
      setError('Please enter a valid URL.');
      return;
    }

    setError('');

    const newItem = { ...site, notetime: new Date() };
    newItem.useremail = userDetails.email;

    try {
      if (site._id !== undefined) {
        const updatedData = await updateSiteData(newItem, showToast);
        setSite(updatedData);

        showToast(
          updatedData?.msg ?? "Site updated successfully!",
          updatedData?.result === "success" ? "success" : "error"
        );
      } else {
        const addedData = await addSiteData(newItem, showToast);

        showToast(
          addedData?.msg ?? "Site added successfully!",
          addedData?.result === "success" ? "success" : "error"
        );
      }

      navigate("/site");
    } catch (error) {
      console.error(error);
      showToast("An error occurred while saving the site", "error");
    }
  };

  return (
    <div>
      <div className="px-2 sticky-header">
        <h3 className="m-0 p-2">{title} | {buttonLabel} Site</h3>
        <AppMenu />
      </div>
      <div className="p-0 m-0 px-2 pb-2">
        <BackButton text="Back" icon="←" />
        <div className="card p-5">
          <form className="container float-center needs-validation">
            <div className="row">
              <div className="col-md-6 col-sm-12 mb-3">
                <label className="form-label">Name</label>
                <input
                  placeholder="Name"
                  name="name"
                  onChange={handleChange}
                  value={site.name || ""}
                  className="form-control"
                  required
                />
              </div>
              <div className="col-md-6 col-sm-12 mb-3">
                <label className="form-label">
                  Link {isDetectingDates && <span className="text-muted fs-6 ms-2">🔄 Auto-detecting dates...</span>}
                </label>
                <input
                  placeholder="https://example.com"
                  name="link"
                  onChange={handleChange}
                  onBlur={handleLinkBlur}
                  value={site.link || ""}
                  className="form-control"
                  required
                />
                <span style={{ color: "red" }}>{error}</span>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 col-sm-12 mb-3">
                <label className="form-label">Associated Server <small className="text-muted">(Optional)</small></label>
                <Select
                  name="serverId"
                  className="basic-single mb-0"
                  placeholder="Select Server (or leave unassigned)"
                  isClearable={true}
                  options={serverOptions}
                  onChange={handleServerChange}
                  value={
                    serverOptions.find((s) => String(s.value) === String(site.serverId) || s.name === site.serverName || (site.serverId && s.name === site.serverId)) ||
                    (site.serverName || site.serverId ? { label: site.serverName || site.serverId, value: site.serverId || site.serverName } : null)
                  }
                />
              </div>
              <div className="col-md-6 col-sm-12 mb-3">
                <label className="form-label">Running Status</label>
                <Select
                  name="runningStatus"
                  className="basic-single mb-0"
                  placeholder="Select Running Status"
                  options={runningStatusOptions}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.name}
                  onChange={handleRunningStatusChange}
                  autoFocus={false}
                  value={runningStatusOptions.find((i) => i.value === site.runningStatus)}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 col-sm-6 mb-3">
                <label className="form-label">
                  Cert. Exp. Date <small className="text-muted">(Auto-filled)</small>
                </label>
                <input
                  type="date"
                  placeholder="Date"
                  name="sslExpiry"
                  onChange={handleChange}
                  value={site.sslExpiry ? (typeof site.sslExpiry === 'string' ? site.sslExpiry.split('T')[0] : new Date(site.sslExpiry).toISOString().split('T')[0]) : ''}
                  className="form-control"
                />
              </div>
              <div className="col-md-6 col-sm-6 mb-3">
                <label className="form-label">
                  Domain Exp. Date <small className="text-muted">(Auto-filled)</small>
                </label>
                <input
                  type="date"
                  placeholder="Date"
                  name="domainExpiry"
                  onChange={handleChange}
                  value={site.domainExpiry ? (typeof site.domainExpiry === 'string' ? site.domainExpiry.split('T')[0] : new Date(site.domainExpiry).toISOString().split('T')[0]) : ''}
                  className="form-control"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 col-sm-12 mb-3">
                <label className="form-label">Comment</label>
                <input
                  placeholder="Comment"
                  name="comment"
                  onChange={handleChange}
                  value={site.comment || ""}
                  className="form-control"
                />
              </div>
              <div className="col-md-6 col-sm-12 mb-3">
                <label className="form-label">Contact</label>
                <input
                  placeholder="Contact"
                  name="contact"
                  onChange={handleChange}
                  value={site.contact || ""}
                  className="form-control"
                />
              </div>
            </div>
            <br />
            <div className="row">
              <div className="col-md-2 col-sm-6">
                <input
                  type="button"
                  onClick={handleSubmit}
                  value={buttonLabel}
                  className="btn btn-primary btn-block w-100"
                />
              </div>
              <div className="col-md-2 col-sm-6">
                <input
                  type="button"
                  value="Cancel"
                  className="btn btn-block btn-secondary w-100"
                  onClick={() => {
                    navigate("/site");
                  }}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
